import { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { auth } from "@/lib/auth";
import { toast } from "sonner";
import { downloadCSV } from "@/lib/csv";
import { LogOut, Plus, Trash2, Save, Image as ImageIcon, Upload, X, Download, Calendar } from "lucide-react";

const TABS = ["products", "blog", "leads", "carts", "settings"];

export default function AdminDashboard() {
  const [tab, setTab] = useState("products");
  const [me, setMe] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    if (!auth.isAuthed()) { nav("/admin/login"); return; }
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => { auth.clear(); nav("/admin/login"); });
  }, [nav]);

  const signOut = () => { auth.clear(); nav("/admin/login"); };

  if (!me) return null;

  return (
    <div className="min-h-screen bg-warmwhite text-charcoal" data-testid="admin-dashboard">
      <header className="border-b border-taupe/30 bg-warmwhite/90 backdrop-blur sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2">
            <span className="font-serif text-2xl">James</span>
            <span className="font-serif italic text-gold text-xl">1:1</span>
            <span className="ml-2 text-[10px] uppercase tracking-widest-custom text-charcoal/50">Admin</span>
          </Link>
          <div className="flex items-center gap-6 text-sm">
            <span className="hidden md:inline text-charcoal/60 text-xs">{me.email}</span>
            <button onClick={signOut} className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest-custom hover:text-gold transition-colors" data-testid="admin-sign-out">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
        <nav className="max-w-[1400px] mx-auto px-6 md:px-12 flex gap-8 -mb-px">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              data-testid={`admin-tab-${t}`}
              className={`pb-4 text-[11px] uppercase tracking-widest-custom border-b-2 transition-colors ${tab === t ? "border-gold text-charcoal" : "border-transparent text-charcoal/55 hover:text-charcoal"}`}
            >
              {t}
            </button>
          ))}
        </nav>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 md:px-12 py-12">
        {tab === "products" && <ProductsPanel />}
        {tab === "blog" && <BlogPanel />}
        {tab === "leads" && <LeadsPanel />}
        {tab === "carts" && <CartsPanel />}
        {tab === "settings" && <SettingsPanel />}
      </main>
    </div>
  );
}

/* ---------------- Products ---------------- */
function ProductsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // product or "new"
  const fileRef = useRef(null);

  const load = () => api.get("/products").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing && editing.id && editing._mode !== "new") {
        await api.put(`/admin/products/${editing.id}`, data);
        toast.success("Product updated");
      } else {
        await api.post(`/admin/products`, data);
        toast.success("Product created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    try {
      await api.delete(`/admin/products/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  return (
    <section data-testid="admin-products-panel">
      <Header title="Products" count={items.length}
        action={<button onClick={() => setEditing({ _mode: "new", name: "", description: "", price: 0, category: "service", tier: "", scope: "", image: "", delivery: "" })} className="inline-flex items-center gap-2 bg-charcoal text-warmwhite px-5 py-3 text-[11px] uppercase tracking-widest-custom hover:bg-gold hover:text-charcoal transition-colors" data-testid="product-new"><Plus className="w-4 h-4" />New product</button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} className="border border-taupe/40 p-6 bg-warmwhite" data-testid={`admin-product-${p.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[10px] uppercase tracking-widest-custom text-olive">{p.scope || p.category} · {p.tier}</p>
                <h3 className="font-serif text-xl mt-1">{p.name}</h3>
              </div>
              <span className="font-serif text-xl">{p.price === 0 ? "Free" : `$${p.price}`}</span>
            </div>
            <p className="mt-3 text-sm text-charcoal/65 line-clamp-2">{p.description}</p>
            <div className="mt-5 flex items-center gap-3 text-xs">
              <button onClick={() => setEditing(p)} className="px-3 py-2 border border-taupe hover:border-charcoal" data-testid={`product-edit-${p.id}`}>Edit</button>
              <button onClick={() => remove(p.id)} className="px-3 py-2 border border-taupe text-charcoal/70 hover:border-destructive hover:text-destructive inline-flex items-center gap-1" data-testid={`product-delete-${p.id}`}><Trash2 className="w-3.5 h-3.5" />Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditorModal title={editing._mode === "new" ? "New product" : "Edit product"} onClose={() => setEditing(null)}>
          <ProductForm value={editing} onSave={save} onClose={() => setEditing(null)} />
        </EditorModal>
      )}
    </section>
  );
}

function ProductForm({ value, onSave, onClose }) {
  const [form, setForm] = useState({ ...value });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image: url }));
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally { setUploading(false); }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, price: parseFloat(form.price) || 0 }); }} className="space-y-5" data-testid="product-form">
      <Field label="Name" value={form.name} onChange={upd("name")} required testid="product-form-name" />
      <Field label="Description" textarea value={form.description} onChange={upd("description")} required testid="product-form-description" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Price (USD)" type="number" step="0.01" value={form.price} onChange={upd("price")} required testid="product-form-price" />
        <SelectField label="Category" value={form.category} onChange={upd("category")} options={["service", "digital"]} testid="product-form-category" />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Tier" value={form.tier || ""} onChange={upd("tier")} testid="product-form-tier" />
        <Field label="Scope" value={form.scope || ""} onChange={upd("scope")} testid="product-form-scope" />
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">Image URL</label>
        <div className="flex items-center gap-3 mt-2">
          <input value={form.image || ""} onChange={upd("image")} className="flex-1 bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-base" data-testid="product-form-image" />
          <input ref={fileRef} type="file" hidden accept="image/*" onChange={upload} />
          <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 border border-taupe px-3 py-2 text-[11px] uppercase tracking-widest-custom hover:border-charcoal disabled:opacity-50" disabled={uploading} data-testid="product-form-upload">
            <Upload className="w-3.5 h-3.5" />{uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
        {form.image && <img src={form.image} alt="" className="mt-3 max-h-32 object-cover border border-taupe/30" />}
      </div>
      <Field label="Delivery info" textarea value={form.delivery || ""} onChange={upd("delivery")} testid="product-form-delivery" />
      <div className="flex justify-end gap-3 pt-4 border-t border-taupe/30">
        <button type="button" onClick={onClose} className="px-5 py-3 text-[11px] uppercase tracking-widest-custom border border-taupe hover:border-charcoal">Cancel</button>
        <button type="submit" className="px-6 py-3 text-[11px] uppercase tracking-widest-custom bg-charcoal text-warmwhite hover:bg-gold hover:text-charcoal inline-flex items-center gap-2" data-testid="product-form-save"><Save className="w-4 h-4" />Save</button>
      </div>
    </form>
  );
}

/* ---------------- Blog ---------------- */
function BlogPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/blog").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing && editing._mode !== "new") {
        await api.put(`/admin/blog/${editing.slug}`, data);
        toast.success("Post updated");
      } else {
        await api.post(`/admin/blog`, data);
        toast.success("Post created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (slug) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/blog/${slug}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <section data-testid="admin-blog-panel">
      <Header title="Journal posts" count={items.length}
        action={<button onClick={() => setEditing({ _mode: "new", slug: "", title: "", category: "Faith", excerpt: "", body: "", image: "", author: "James Douglas", published_at: new Date().toISOString() })} className="inline-flex items-center gap-2 bg-charcoal text-warmwhite px-5 py-3 text-[11px] uppercase tracking-widest-custom hover:bg-gold hover:text-charcoal transition-colors" data-testid="blog-new"><Plus className="w-4 h-4" />New post</button>}
      />

      <div className="space-y-3">
        {items.map((p) => (
          <div key={p.id || p.slug} className="border border-taupe/40 p-5 flex items-center gap-5 bg-warmwhite" data-testid={`admin-blog-${p.slug}`}>
            {p.image ? <img src={p.image} alt="" className="w-20 h-20 object-cover" /> : <div className="w-20 h-20 bg-taupe/20 flex items-center justify-center"><ImageIcon className="w-6 h-6 text-charcoal/40" /></div>}
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest-custom text-olive">{p.category}</p>
              <h3 className="font-serif text-xl truncate">{p.title}</h3>
              <p className="text-xs text-charcoal/50 truncate">{p.slug}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setEditing(p)} className="px-3 py-2 text-xs border border-taupe hover:border-charcoal" data-testid={`blog-edit-${p.slug}`}>Edit</button>
              <button onClick={() => remove(p.slug)} className="px-3 py-2 text-xs border border-taupe text-charcoal/70 hover:border-destructive hover:text-destructive inline-flex items-center gap-1" data-testid={`blog-delete-${p.slug}`}><Trash2 className="w-3.5 h-3.5" />Delete</button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <EditorModal title={editing._mode === "new" ? "New post" : "Edit post"} onClose={() => setEditing(null)}>
          <BlogForm value={editing} onSave={save} onClose={() => setEditing(null)} />
        </EditorModal>
      )}
    </section>
  );
}

function BlogForm({ value, onSave, onClose }) {
  const CATS = ["Faith", "Business", "Travel", "Music", "Photography", "Leadership", "AI"];
  const [form, setForm] = useState({ ...value });
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = data.url.startsWith("http") ? data.url : `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      setForm((f) => ({ ...f, image: url }));
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave({ ...form, slug: form.slug || slugify(form.title) }); }} className="space-y-5" data-testid="blog-form">
      <Field label="Title" value={form.title} onChange={upd("title")} required testid="blog-form-title" />
      <div className="grid grid-cols-2 gap-4">
        <Field label="Slug" value={form.slug} onChange={upd("slug")} placeholder="(auto from title if blank)" testid="blog-form-slug" />
        <SelectField label="Category" value={form.category} onChange={upd("category")} options={CATS} testid="blog-form-category" />
      </div>
      <Field label="Excerpt" textarea value={form.excerpt} onChange={upd("excerpt")} required testid="blog-form-excerpt" />
      <Field label="Body" textarea rows={8} value={form.body} onChange={upd("body")} required testid="blog-form-body" />
      <div>
        <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">Hero image</label>
        <div className="flex items-center gap-3 mt-2">
          <input value={form.image || ""} onChange={upd("image")} className="flex-1 bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-base" data-testid="blog-form-image" />
          <input ref={fileRef} type="file" hidden accept="image/*" onChange={upload} />
          <button type="button" onClick={() => fileRef.current?.click()} className="inline-flex items-center gap-2 border border-taupe px-3 py-2 text-[11px] uppercase tracking-widest-custom hover:border-charcoal disabled:opacity-50" disabled={uploading} data-testid="blog-form-upload">
            <Upload className="w-3.5 h-3.5" />{uploading ? "Uploading…" : "Upload"}
          </button>
        </div>
        {form.image && <img src={form.image} alt="" className="mt-3 max-h-32 object-cover border border-taupe/30" />}
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Author" value={form.author || ""} onChange={upd("author")} testid="blog-form-author" />
        <Field label="Published at (ISO)" value={form.published_at || ""} onChange={upd("published_at")} testid="blog-form-published" />
      </div>
      <div className="flex justify-end gap-3 pt-4 border-t border-taupe/30">
        <button type="button" onClick={onClose} className="px-5 py-3 text-[11px] uppercase tracking-widest-custom border border-taupe hover:border-charcoal">Cancel</button>
        <button type="submit" className="px-6 py-3 text-[11px] uppercase tracking-widest-custom bg-charcoal text-warmwhite hover:bg-gold hover:text-charcoal inline-flex items-center gap-2" data-testid="blog-form-save"><Save className="w-4 h-4" />Save</button>
      </div>
    </form>
  );
}

/* ---------------- Leads (Contacts + Bookings + Subscribers) ---------------- */
function LeadsPanel() {
  const [tab, setTab] = useState("contacts");
  const [contacts, setContacts] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [subs, setSubs] = useState([]);

  useEffect(() => {
    api.get("/admin/contacts").then((r) => setContacts(r.data)).catch(() => {});
    api.get("/admin/bookings").then((r) => setBookings(r.data)).catch(() => {});
    api.get("/admin/subscribers").then((r) => setSubs(r.data)).catch(() => {});
  }, []);

  const SUBTABS = [
    { id: "contacts", label: "Contacts", count: contacts.length, rows: contacts, cols: ["created_at", "first_name", "last_name", "email", "phone", "subject", "message"] },
    { id: "bookings", label: "Bookings", count: bookings.length, rows: bookings, cols: ["created_at", "full_name", "email", "phone", "service", "preferred_date", "details"] },
    { id: "subscribers", label: "Subscribers", count: subs.length, rows: subs, cols: ["created_at", "email", "first_name"] },
  ];
  const current = SUBTABS.find((t) => t.id === tab);

  const exportCSV = () => {
    const date = new Date().toISOString().slice(0, 10);
    downloadCSV(`james1to1-${current.id}-${date}.csv`, current.rows, current.cols);
    toast.success(`Exported ${current.rows.length} ${current.id}`);
  };

  return (
    <section data-testid="admin-leads-panel">
      <Header title="Leads & submissions" count={contacts.length + bookings.length + subs.length}
        action={
          <button onClick={exportCSV} disabled={!current.rows.length} className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-5 py-3 text-[11px] uppercase tracking-widest-custom hover:bg-charcoal hover:text-warmwhite transition-colors disabled:opacity-40" data-testid={`leads-export-${tab}`}>
            <Download className="w-4 h-4" /> Export {current.label} CSV
          </button>
        }
      />
      <div className="flex gap-2 mb-8" data-testid="leads-subtabs">
        {SUBTABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            data-testid={`leads-subtab-${t.id}`}
            className={`px-4 py-2 text-[11px] uppercase tracking-widest-custom border transition-colors ${
              tab === t.id ? "border-charcoal bg-charcoal text-warmwhite" : "border-taupe text-charcoal/70 hover:border-charcoal"
            }`}
          >
            {t.label} · {t.count}
          </button>
        ))}
      </div>

      {tab === "contacts" && (
        <div className="space-y-3">
          {contacts.length === 0 && <p className="text-charcoal/50">No contact submissions yet.</p>}
          {contacts.map((c) => (
            <div key={c.id} className="border border-taupe/40 p-5 bg-warmwhite" data-testid={`admin-contact-${c.id}`}>
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-serif text-lg">{c.first_name} {c.last_name}</p>
                <p className="text-[11px] uppercase tracking-widest-custom text-charcoal/50">{new Date(c.created_at).toLocaleString()}</p>
              </div>
              <p className="text-sm text-charcoal/65 mt-1">{c.email} · {c.phone || "no phone"} · {c.subject}</p>
              <p className="mt-3 text-sm leading-relaxed whitespace-pre-line">{c.message}</p>
            </div>
          ))}
        </div>
      )}

      {tab === "bookings" && (
        <div className="space-y-3">
          {bookings.length === 0 && <p className="text-charcoal/50">No bookings yet.</p>}
          {bookings.map((b) => (
            <div key={b.id} className="border border-taupe/40 p-5 bg-warmwhite" data-testid={`admin-booking-${b.id}`}>
              <div className="flex items-baseline justify-between gap-4">
                <p className="font-serif text-lg">{b.full_name}</p>
                <p className="text-[11px] uppercase tracking-widest-custom text-charcoal/50">{new Date(b.created_at).toLocaleString()}</p>
              </div>
              <p className="text-sm text-charcoal/65 mt-1">{b.email} · {b.phone || "no phone"} · {b.service}</p>
              {b.preferred_date && <p className="text-xs text-charcoal/55 mt-1">Preferred: {b.preferred_date}</p>}
              {b.details && <p className="mt-3 text-sm leading-relaxed whitespace-pre-line">{b.details}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === "subscribers" && (
        <div className="space-y-2">
          {subs.length === 0 && <p className="text-charcoal/50">No newsletter subscribers yet.</p>}
          {subs.map((s) => (
            <div key={s.id} className="border border-taupe/40 p-4 bg-warmwhite flex items-center justify-between" data-testid={`admin-subscriber-${s.id}`}>
              <p className="font-serif text-base">{s.email}</p>
              <p className="text-[11px] uppercase tracking-widest-custom text-charcoal/50">{new Date(s.created_at).toLocaleDateString()}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

/* ---------------- Abandoned Carts ---------------- */
function CartsPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/carts")
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const exportCSV = () => {
    const rows = items.map((c) => ({
      created_at: c.created_at,
      last_activity: c.last_activity,
      email: c.email,
      name: c.name,
      completed: c.completed ? "yes" : "no",
      stages_sent: (c.stages_sent || []).join("|"),
      item_count: (c.items || []).reduce((a, i) => a + (i.quantity || 0), 0),
      subtotal: c.subtotal ? `${c.subtotal.amount} ${c.subtotal.currency}` : "",
      checkout_url: c.checkout_url,
    }));
    const cols = ["created_at", "last_activity", "email", "name", "completed", "stages_sent", "item_count", "subtotal", "checkout_url"];
    const date = new Date().toISOString().slice(0, 10);
    downloadCSV(`signal-house-carts-${date}.csv`, rows, cols);
    toast.success(`Exported ${rows.length} carts`);
  };

  const openCount = items.filter((c) => !c.completed).length;
  const recoveredCount = items.filter((c) => c.completed).length;

  return (
    <section data-testid="admin-carts-panel">
      <Header
        title="Abandoned carts"
        count={items.length}
        action={
          <button
            onClick={exportCSV}
            disabled={!items.length}
            className="inline-flex items-center gap-2 border border-charcoal text-charcoal px-5 py-3 text-[11px] uppercase tracking-widest-custom hover:bg-charcoal hover:text-warmwhite transition-colors disabled:opacity-40"
            data-testid="carts-export"
          >
            <Download className="w-4 h-4" /> Export CSV
          </button>
        }
      />

      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="border border-taupe/40 p-5 bg-warmwhite">
          <p className="text-[10px] uppercase tracking-widest-custom text-olive">Open</p>
          <p className="font-serif text-3xl mt-1">{openCount}</p>
        </div>
        <div className="border border-taupe/40 p-5 bg-warmwhite">
          <p className="text-[10px] uppercase tracking-widest-custom text-olive">Recovered / Checked out</p>
          <p className="font-serif text-3xl mt-1">{recoveredCount}</p>
        </div>
        <div className="border border-taupe/40 p-5 bg-warmwhite">
          <p className="text-[10px] uppercase tracking-widest-custom text-olive">Emails dispatched</p>
          <p className="font-serif text-3xl mt-1">{items.reduce((a, c) => a + (c.stages_sent?.length || 0), 0)}</p>
        </div>
      </div>

      {loading && <p className="text-charcoal/50">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="text-charcoal/50">No cart activity yet — captured emails and recovery status will appear here as shoppers save their carts.</p>
      )}

      <div className="space-y-3">
        {items.map((c) => (
          <div key={c.cart_id} className="border border-taupe/40 p-5 bg-warmwhite" data-testid={`admin-cart-${c.cart_id}`}>
            <div className="flex items-baseline justify-between gap-4">
              <div>
                <p className="font-serif text-lg">{c.email}</p>
                <p className="text-xs text-charcoal/55 mt-0.5">
                  {c.name || "Anonymous"} · {(c.items || []).length} {(c.items || []).length === 1 ? "item" : "items"}
                  {c.subtotal ? ` · $${Number(c.subtotal.amount || 0).toFixed(2)} ${(c.subtotal.currency || "USD").toUpperCase()}` : ""}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[11px] uppercase tracking-widest-custom text-charcoal/50">Last activity</p>
                <p className="text-xs text-charcoal/65">{new Date(c.last_activity).toLocaleString()}</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2 items-center">
              {c.completed ? (
                <span className="text-[10px] uppercase tracking-widest-custom px-2 py-1 bg-olive/15 text-olive">Completed</span>
              ) : (
                <span className="text-[10px] uppercase tracking-widest-custom px-2 py-1 bg-gold/15 text-gold">Open</span>
              )}
              {[1, 2, 3].map((s) => (
                <span
                  key={s}
                  className={`text-[10px] uppercase tracking-widest-custom px-2 py-1 border ${
                    (c.stages_sent || []).includes(s)
                      ? "border-olive text-olive"
                      : "border-taupe/50 text-charcoal/40"
                  }`}
                >
                  Stage {s}{(c.stages_sent || []).includes(s) ? " ✓" : ""}
                </span>
              ))}
            </div>

            {c.items?.length > 0 && (
              <ul className="mt-4 divide-y divide-taupe/25">
                {c.items.slice(0, 4).map((it, idx) => (
                  <li key={idx} className="py-2 flex items-center gap-3 text-sm">
                    {it.image ? <img src={it.image} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 bg-taupe/20 rounded" />}
                    <div className="flex-1 min-w-0">
                      <p className="truncate">{it.title}</p>
                      <p className="text-xs text-charcoal/50">Qty {it.quantity}{it.variant_title && it.variant_title !== "Default Title" ? ` · ${it.variant_title}` : ""}</p>
                    </div>
                    {it.price && <span className="text-xs text-charcoal/60">${Number(it.price.amount || 0).toFixed(2)}</span>}
                  </li>
                ))}
                {c.items.length > 4 && <li className="py-2 text-xs text-charcoal/50">+{c.items.length - 4} more…</li>}
              </ul>
            )}

            {c.checkout_url && (
              <a
                href={c.checkout_url}
                target="_blank"
                rel="noreferrer"
                className="inline-block mt-4 text-xs text-gold hover:underline"
              >
                Open checkout link →
              </a>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ---------------- Settings ---------------- */
function SettingsPanel() {
  const [form, setForm] = useState({ calendly_url: "", calendar_enabled: true });
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setForm({
        calendly_url: r.data?.calendly_url || "",
        calendar_enabled: r.data?.calendar_enabled !== false,
      });
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Settings saved.");
    } catch {
      toast.error("Save failed.");
    } finally { setBusy(false); }
  };

  if (!loaded) return <p className="text-charcoal/50">Loading…</p>;

  return (
    <section data-testid="admin-settings-panel">
      <Header title="Settings" count={1} />
      <form onSubmit={save} className="max-w-2xl space-y-8 border border-taupe/40 bg-warmwhite p-8" data-testid="settings-form">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Calendar className="w-5 h-5 text-gold" strokeWidth={1.5} />
            <p className="text-[11px] uppercase tracking-widest-custom text-olive">Scheduling</p>
          </div>
          <h2 className="font-serif text-2xl mb-2">Calendly link</h2>
          <p className="text-sm text-charcoal/65 mb-4">Shown after every paid service order and on the Booking section. Example: <code className="text-xs bg-taupe/20 px-1 py-0.5">https://calendly.com/yourname/30min</code></p>
          <input
            type="url"
            value={form.calendly_url}
            onChange={(e) => setForm({ ...form, calendly_url: e.target.value })}
            placeholder="https://calendly.com/jamesdouglas/30min"
            className="w-full bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-lg"
            data-testid="settings-calendly-url"
          />
          <label className="mt-5 flex items-center gap-3 cursor-pointer text-sm">
            <input
              type="checkbox"
              checked={form.calendar_enabled}
              onChange={(e) => setForm({ ...form, calendar_enabled: e.target.checked })}
              className="w-4 h-4 accent-gold"
              data-testid="settings-calendly-enabled"
            />
            <span className="text-charcoal/80">Show Calendly scheduler on the success page and Booking section</span>
          </label>
        </div>

        <div className="flex justify-end pt-4 border-t border-taupe/30">
          <button type="submit" disabled={busy} className="inline-flex items-center gap-2 bg-charcoal text-warmwhite px-8 py-4 text-[11px] uppercase tracking-widest-custom hover:bg-gold hover:text-charcoal transition-colors disabled:opacity-50" data-testid="settings-save">
            <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save settings"}
          </button>
        </div>
      </form>
    </section>
  );
}

/* ---------------- Reusable ---------------- */
function Header({ title, count, action }) {
  return (
    <div className="flex items-end justify-between mb-8 pb-4 border-b border-taupe/40">
      <div>
        <h1 className="font-serif text-3xl md:text-4xl font-light">{title}</h1>
        <p className="text-[11px] uppercase tracking-widest-custom text-charcoal/55 mt-2">{count} {count === 1 ? "item" : "items"}</p>
      </div>
      {action}
    </div>
  );
}

function EditorModal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 bg-charcoal/40 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="editor-modal">
      <div className="bg-warmwhite max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-8 py-6 border-b border-taupe/40 sticky top-0 bg-warmwhite z-10">
          <h2 className="font-serif text-2xl">{title}</h2>
          <button onClick={onClose} aria-label="Close" className="p-1 hover:text-gold"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-8">{children}</div>
      </div>
    </div>
  );
}

function Field({ label, textarea, rows = 3, type = "text", value, onChange, required, placeholder, testid, step }) {
  const cls = "w-full bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-lg";
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">{label}{required && " *"}</label>
      {textarea ? (
        <textarea rows={rows} value={value ?? ""} onChange={onChange} required={required} placeholder={placeholder} className={cls + " resize-none"} data-testid={testid} />
      ) : (
        <input type={type} step={step} value={value ?? ""} onChange={onChange} required={required} placeholder={placeholder} className={cls} data-testid={testid} />
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest-custom text-charcoal/55">{label}</label>
      <select value={value} onChange={onChange} className="mt-2 w-full bg-transparent border-b border-taupe focus:border-charcoal outline-none py-2 font-serif text-lg" data-testid={testid}>
        {options.map((o) => <option key={o}>{o}</option>)}
      </select>
    </div>
  );
}
